/**
 * finderQuestions.js — the guided "What agreement do I need?" tool.
 *
 * One question: what are you trying to do? Each answer returns a short, ranked answer set rather
 * than the founder's whole document plan. The intake wizard is for the whole plan; this is for the
 * founder who has one thing in front of them right now.
 *
 * `followUp` optionally asks one more question to split a broad intent.
 */

export const FINDER_INTENTS = [
  {
    id: "start-a-company",
    label: "Start a company",
    icon: "Building2",
    group: "Company & ownership",
    lead: "Forming the entity is the filing. These are the documents the filing does not create.",
    docs: ["founder-agreement", "llc-operating-agreement", "corporate-bylaws", "founder-ip-assignment", "board-consent"],
    followUp: {
      question: "Which entity?",
      options: [
        { value: "llc", label: "LLC", docs: ["llc-operating-agreement", "single-member-llc-operating-agreement", "multi-member-llc-operating-agreement", "written-consent-of-members"] },
        { value: "corporation", label: "Corporation", docs: ["corporate-bylaws", "board-consent", "founder-stock-purchase-agreement", "written-consent-of-shareholders", "officer-appointment-resolution"] },
        { value: "unsure", label: "Not sure yet", docs: ["founder-agreement", "llc-operating-agreement", "corporate-bylaws"] },
      ],
    },
  },
  {
    id: "bring-in-cofounder",
    label: "Bring in a co-founder",
    icon: "Users",
    group: "Company & ownership",
    lead: "Split equity, set vesting, and make sure their work belongs to the company.",
    docs: ["co-founder-agreement", "founder-vesting-agreement", "founder-ip-assignment", "piia", "buy-sell-agreement"],
  },
  {
    id: "give-equity",
    label: "Give someone equity",
    icon: "PieChart",
    group: "Company & ownership",
    lead: "Equity promised verbally is the single most common cap-table problem in early CPG.",
    docs: ["restricted-stock-purchase-agreement", "stock-option-agreement", "equity-incentive-plan", "advisor-equity-agreement", "vesting-agreement", "cap-table-documentation"],
  },
  {
    id: "hire-employee",
    label: "Hire an employee",
    icon: "UserPlus",
    group: "People",
    lead: "Employment terms vary by jurisdiction — these are the pieces, not a finished package.",
    docs: ["employment-agreement", "offer-letter", "employee-confidentiality-agreement", "employee-ip-assignment-agreement", "employee-nda"],
  },
  {
    id: "hire-contractor",
    label: "Hire a contractor",
    icon: "Wrench",
    group: "People",
    lead: "The label on the contract does not settle whether someone is a contractor. What they make still has to be assigned.",
    docs: ["independent-contractor-agreement", "contractor-nda", "contractor-work-for-hire-agreement", "work-for-hire-agreement", "ip-assignment-agreement"],
  },
  {
    id: "protect-an-idea",
    label: "Protect an idea",
    icon: "Lock",
    group: "Confidentiality & IP",
    lead: "Confidentiality stops them repeating it. Non-use stops them doing it themselves. Those are different clauses.",
    docs: ["mutual-nda", "one-way-nda", "non-disclosure-non-use-agreement", "trade-secret-protection-agreement", "confidentiality-agreement"],
  },
  {
    id: "develop-a-product",
    label: "Develop a product",
    icon: "FlaskConical",
    group: "Product",
    lead: "Decide who owns what gets invented before the work starts.",
    docs: ["product-development-nda", "product-development-agreement", "formula-ownership-agreement", "product-development-ip-agreement", "rd-agreement"],
  },
  {
    id: "hire-food-scientist",
    label: "Hire a food scientist",
    icon: "Microscope",
    group: "Product",
    lead: "The person who writes the formula must assign it, in writing, to the company.",
    docs: ["food-scientist-agreement", "beverage-formulator-agreement", "formula-development-agreement", "formula-assignment-agreement", "product-development-nda"],
  },
  {
    id: "hire-flavor-house",
    label: "Hire a flavor house",
    icon: "Beaker",
    group: "Product",
    lead: "Flavor houses commonly keep ownership of the flavor system and sell it to you as an ingredient. Know that going in.",
    docs: ["flavor-house-agreement", "formula-ownership-agreement", "recipe-formula-confidentiality-agreement", "ingredient-supply-agreement", "trade-secret-protection-agreement"],
  },
  {
    id: "hire-copacker",
    label: "Hire a co-packer",
    icon: "Factory",
    group: "Manufacturing",
    lead: "The contract your entire business depends on. Read termination before you read pricing.",
    docs: ["co-packer-agreement", "manufacturing-nda", "quality-agreement", "product-specification-sheet", "recall-responsibility-agreement", "formula-ownership-agreement"],
  },
  {
    id: "buy-ingredients",
    label: "Purchase ingredients",
    icon: "Package",
    group: "Suppliers",
    lead: "Price escalation and minimum commitments outlive the enthusiasm that made you sign them.",
    docs: ["ingredient-supply-agreement", "raw-material-supply-agreement", "master-supply-agreement", "supplier-quality-agreement", "purchase-order-terms"],
  },
  {
    id: "buy-packaging",
    label: "Purchase packaging",
    icon: "Box",
    group: "Suppliers",
    lead: "Long lead times, large minimums, and a label change that can strand the whole order.",
    docs: ["packaging-supply-agreement", "can-supply-agreement", "bottle-supply-agreement", "label-supply-agreement", "carton-supply-agreement", "packaging-specification-agreement"],
  },
  {
    id: "hire-warehouse",
    label: "Hire a warehouse",
    icon: "Warehouse",
    group: "Logistics",
    lead: "Who is accountable when the cycle count comes up short, and who pays to get your inventory out.",
    docs: ["warehouse-agreement", "logistics-services-agreement", "transportation-agreement", "three-pl-agreement"],
  },
  {
    id: "hire-3pl",
    label: "Hire a 3PL",
    icon: "Boxes",
    group: "Logistics",
    lead: "Model the fully loaded per-order cost against your real basket size before signing.",
    docs: ["three-pl-agreement", "fulfillment-agreement", "ecommerce-fulfillment-agreement", "pick-and-pack-agreement", "logistics-services-agreement"],
  },
  {
    id: "appoint-distributor",
    label: "Appoint a distributor",
    icon: "Truck",
    group: "Sales channels",
    lead: "Exclusivity without performance minimums and a clean exit can freeze a territory for years.",
    docs: ["beverage-distributor-agreement", "food-distributor-agreement", "dsd-distributor-agreement", "exclusive-distribution-agreement", "non-exclusive-distribution-agreement", "territory-distribution-agreement"],
    followUp: {
      question: "What are you selling?",
      options: [
        { value: "beverage", label: "A beverage", docs: ["beverage-distributor-agreement", "dsd-distributor-agreement", "territory-distribution-agreement", "exclusive-distribution-agreement"] },
        { value: "food", label: "Food, snacks or supplements", docs: ["food-distributor-agreement", "wholesale-distribution-agreement", "territory-distribution-agreement"] },
        { value: "international", label: "Selling into another country", docs: ["international-distributor-agreement", "importer-agreement", "international-trademark-license-agreement"] },
      ],
    },
  },
  {
    id: "hire-broker",
    label: "Hire a sales broker",
    icon: "Handshake",
    group: "Sales channels",
    lead: "Define house accounts on day one, or pay commission on business you brought in yourself.",
    docs: ["cpg-broker-agreement", "retail-broker-agreement", "foodservice-broker-agreement", "commission-sales-agreement", "key-account-sales-agreement"],
  },
  {
    id: "sell-to-retailers",
    label: "Sell to retailers",
    icon: "Store",
    group: "Sales channels",
    lead: "The purchase order is not the deal. The vendor agreement and the deduction policy are.",
    docs: ["retail-vendor-agreement", "wholesale-supply-agreement", "retailer-supply-agreement", "promotional-allowance-agreement", "slotting-placement-agreement", "consignment-agreement"],
  },
  {
    id: "hire-agency",
    label: "Hire a marketing agency",
    icon: "Megaphone",
    group: "Marketing",
    lead: "Settle who owns the ad account, the pixel, the audiences and the creative before you start.",
    docs: ["marketing-agency-agreement", "advertising-agency-agreement", "branding-agency-agreement", "social-media-management-agreement", "work-for-hire-agreement"],
  },
  {
    id: "hire-influencer",
    label: "Hire an influencer",
    icon: "Sparkles",
    group: "Marketing",
    lead: "Organic use, paid-ad use and whitelisting are three separate grants. Buy the asset, not the post.",
    docs: ["influencer-agreement", "ugc-creator-agreement", "content-creator-agreement", "influencer-disclosure", "nil-release", "content-release"],
  },
  {
    id: "create-affiliate-program",
    label: "Create an affiliate program",
    icon: "Share2",
    group: "Marketing",
    lead: "Coupon stacking and brand-term bidding are how affiliate programs quietly lose money.",
    docs: ["affiliate-agreement", "influencer-affiliate-agreement", "referral-agreement", "affiliate-disclosure", "commission-referral-agreement"],
  },
  {
    id: "license-a-brand",
    label: "License a brand",
    icon: "BadgeCheck",
    group: "Partnerships",
    lead: "Approval rights and quality control are the licensor's mechanism for protecting the mark, and they collide with booked production time.",
    docs: ["trademark-license-agreement", "brand-license-agreement", "co-branding-agreement", "collaboration-agreement", "product-collaboration-agreement"],
  },
  {
    id: "license-a-celebrity",
    label: "License a celebrity or athlete",
    icon: "Star",
    group: "Partnerships",
    lead: "A morals clause can strand printed packaging overnight. Plan for what happens to inventory.",
    docs: ["celebrity-license-agreement", "nil-agreement", "nil-release", "sponsorship-agreement", "ambassador-agreement"],
  },
  {
    id: "raise-money",
    label: "Raise money",
    icon: "TrendingUp",
    group: "Money",
    lead: "Securities documents. Bring these to counsel — this library is here so you arrive prepared.",
    attorneyLed: true,
    docs: ["term-sheet", "safe", "convertible-note", "preferred-stock-purchase-agreement", "subscription-agreement", "cap-table-documentation"],
  },
  {
    id: "borrow-money",
    label: "Borrow money",
    icon: "Landmark",
    group: "Money",
    lead: "Understand what the lender's paperwork reaches — including your inventory and, often, you personally.",
    docs: ["business-loan-agreement", "promissory-note", "line-of-credit-agreement", "security-agreement", "personal-guarantee", "inventory-financing-agreement"],
  },
  {
    id: "launch-website",
    label: "Launch a website",
    icon: "Globe",
    group: "Online",
    lead: "Copying another brand's policies describes practices you do not follow, which is worse than having none.",
    attorneyLed: true,
    docs: ["website-terms-of-use", "terms-and-conditions", "privacy-policy", "shipping-policy", "refund-policy", "cookie-policy"],
    followUp: {
      question: "Selling on a subscription?",
      options: [
        { value: "yes", label: "Yes", docs: ["subscription-terms", "auto-renewal-terms", "refund-policy", "sms-marketing-terms", "privacy-policy"] },
        { value: "no", label: "No", docs: ["website-terms-of-use", "terms-and-conditions", "privacy-policy", "refund-policy", "shipping-policy"] },
      ],
    },
  },
  {
    id: "run-a-promotion",
    label: "Run a promotion",
    icon: "PartyPopper",
    group: "Online",
    lead: "Contest and sweepstakes requirements vary by jurisdiction and are commonly missed. Settle the mechanic before you commit artwork or media.",
    docs: ["contest-rules", "sweepstakes-rules", "product-giveaway-rules", "promotional-agreement", "event-sampling-agreement", "demo-agreement"],
  },
  {
    id: "terminate-relationship",
    label: "Terminate a business relationship",
    icon: "ShieldAlert",
    group: "Problems",
    lead: "Read the notice clause before you send anything. Wrong method, wrong address or wrong cure period wastes the right you are exercising.",
    docs: ["notice-to-cure", "breach-notice", "notice-of-termination", "termination-agreement", "mutual-termination-agreement", "demand-letter"],
  },
  {
    id: "sell-my-company",
    label: "Sell my company",
    icon: "Briefcase",
    group: "Exit",
    lead: "Most of the work is preparation. Diligence surfaces every agreement you never signed.",
    attorneyLed: true,
    docs: ["due-diligence-nda", "letter-of-intent", "no-shop-agreement", "asset-purchase-agreement", "ip-purchase-agreement", "transition-services-agreement"],
  },
];

export const FINDER_BY_ID = Object.fromEntries(FINDER_INTENTS.map((i) => [i.id, i]));
export const FINDER_GROUPS = [...new Set(FINDER_INTENTS.map((i) => i.group))];

/** Resolve an intent (and optional follow-up answer) to a document id list. */
export function finderResult(intentId, followUpValue) {
  const intent = FINDER_BY_ID[intentId];
  if (!intent) return { intent: null, docIds: [] };
  if (followUpValue && intent.followUp) {
    const opt = intent.followUp.options.find((o) => o.value === followUpValue);
    if (opt) return { intent, docIds: opt.docs, followUpValue };
  }
  return { intent, docIds: intent.docs };
}
